// SmsSuppressionsView — TCPA-side mirror of EmailSuppressionsView.
//
// Per-agency view of the SmsSuppression table ("No-Textear"). Lets the
// agency owner / solo agent see which numbers are excluded from texting
// (because the recipient replied STOP/BAJA, or an owner added them by hand)
// and manually add or remove entries.
//
// AGENTs and SEATs can see the list AND add entries (recording an opt-out
// only ever blocks texting, so it's compliance-safe for every role —
// Lissette 2026-07-31). Only owner/solo can REMOVE an entry, since
// unsuppressing re-enables texting someone who opted out (TCPA).

import { useEffect, useState, useCallback } from 'react';
import { Search, PhoneOff, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { listSmsSuppressions, addSmsSuppression, removeSmsSuppression } from '../api.js';
import { toast } from './Toast.jsx';
import { confirmDialog, ClientSearchSelect } from './ui.jsx';

const SOURCE_LABELS_EN = {
  stop_reply: 'Recipient texted STOP',
  admin: 'Manually added by agency',
};
const SOURCE_LABELS_ES = {
  stop_reply: 'Cliente respondió STOP/BAJA',
  admin: 'Agregado manualmente por la agencia',
};

const SOURCE_BADGE = {
  stop_reply: 'bg-blue-500/20 text-blue-200 border-blue-500/30',
  admin: 'bg-slate-500/20 text-slate-200 border-slate-500/30',
};

// Display a stored 10-digit key as (XXX) XXX-XXXX; leave anything else as-is.
function fmtPhone(raw) {
  const d = String(raw || '').replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return raw || '';
}

export function SmsSuppressionsView({ currentUser, clients = [], lang = 'en' }) {
  const es = lang === 'es';
  const role = currentUser?.role || '';
  // Removing re-enables texting an opted-out recipient → owner/solo only.
  // Adding is open to every role (backend enforces the same split).
  const canRemove = role === 'agency_owner' || role === 'solo_agent' || currentUser?.isPlatformOwner;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [newReason, setNewReason] = useState('');
  const [pickedClientId, setPickedClientId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // #8: pick a CRM client to auto-fill their phone (and a reason) instead of
  // typing the number by hand.
  const onPickClient = (e) => {
    const id = e.target.value;
    setPickedClientId(id);
    const c = (clients || []).find((x) => x.id === id);
    if (!c) return;
    if (c.phone) setNewPhone(c.phone);
    else toast.info(es ? 'Ese cliente no tiene teléfono guardado — escríbelo abajo' : 'That client has no saved phone — type it below');
    const full = `${c.firstName || ''} ${c.lastName || ''}`.trim();
    if (full && !newReason.trim()) setNewReason((es ? 'Cliente: ' : 'Client: ') + full);
  };

  const SOURCE_LABELS = es ? SOURCE_LABELS_ES : SOURCE_LABELS_EN;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listSmsSuppressions({ search, limit: 200 });
      setRows(res.data);
      setTotal(res.total);
    } catch (err) {
      setError(err.message || (es ? 'No se pudo cargar la lista' : 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [search, es]);

  useEffect(() => {
    const t = setTimeout(load, 200); // debounce search
    return () => clearTimeout(t);
  }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const digits = newPhone.replace(/\D/g, '');
    if (digits.length < 10) {
      toast.error(es ? 'Ingresa un número de 10 dígitos' : 'Enter a valid 10-digit number');
      return;
    }
    setSubmitting(true);
    try {
      await addSmsSuppression({ phone: newPhone, reason: newReason.trim() || undefined });
      toast.success(es ? 'Agregado a la lista de no-textear' : 'Added to Do-Not-Text list');
      setNewPhone('');
      setNewReason('');
      setPickedClientId('');
      setShowAdd(false);
      load();
    } catch (err) {
      toast.error(err.message || (es ? 'No se pudo agregar' : 'Failed to add'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (row) => {
    const ok = await confirmDialog(
      es
        ? `¿Quitar a ${fmtPhone(row.phone)} de la lista de no-textear?\n\nEl sistema podrá enviarle SMS otra vez. Esta acción queda registrada en el audit log.`
        : `Remove ${fmtPhone(row.phone)} from the Do-Not-Text list?\n\nThe system will be able to text this number again. This action is recorded in the audit log.`,
    );
    if (!ok) return;
    try {
      await removeSmsSuppression(row.id);
      toast.success(es ? 'Eliminado de la lista' : 'Removed from Do-Not-Text list');
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setTotal((t) => Math.max(0, t - 1));
    } catch (err) {
      toast.error(err.message || (es ? 'No se pudo eliminar' : 'Failed to remove'));
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
            <PhoneOff className="w-6 h-6 text-rose-400" />
            {es ? 'Lista de No-Textear (SMS)' : 'Do-Not-Text List (SMS)'}
          </h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            {es
              ? 'Números que respondieron "STOP/BAJA" o que agregaste a mano. El sistema no les envía SMS: ni campañas, ni recordatorios automáticos, ni textos 1:1. Cumple con TCPA.'
              : 'Numbers that replied "STOP" or that you added manually. The system blocks all SMS to them — campaigns, automated reminders and 1:1 texts. TCPA compliance.'}
          </p>
        </div>
        <button
          onClick={load}
          className="p-2 text-slate-400 hover:text-slate-100 transition"
          title={es ? 'Actualizar' : 'Refresh'}
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={es ? 'Buscar número…' : 'Search number…'}
            className="w-full pl-10 pr-3 py-2 bg-slate-800/60 border border-slate-700/50 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
          />
        </div>
        <button
          onClick={() => setShowAdd((v) => !v)}
          className="px-4 py-2 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold rounded-lg text-sm flex items-center gap-2 transition"
        >
          <Plus className="w-4 h-4" />
          {es ? 'Agregar manualmente' : 'Add manually'}
        </button>
      </div>

      {showAdd && (
        <form
          onSubmit={handleAdd}
          className="mt-4 p-4 bg-slate-800/40 border border-slate-700/50 rounded-lg space-y-3"
        >
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1">
              {es ? 'Buscar cliente del CRM (opcional)' : 'Search a CRM client (optional)'}
            </label>
            <ClientSearchSelect
              clients={clients}
              value={pickedClientId}
              onChange={onPickClient}
              lang={lang}
              noneLabel={es ? '— Escribir a mano —' : '— Type manually —'}
            />
            <div className="text-[11px] text-slate-400 mt-1">
              {es ? 'Elige un cliente y se llena su teléfono automáticamente.' : 'Pick a client and their phone fills in automatically.'}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1">
              {es ? 'Número de teléfono' : 'Phone number'}
            </label>
            <input
              type="tel"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder="(954) 937-4843"
              required
              autoFocus
              className="w-full px-3 py-2 bg-slate-900/60 border border-slate-700/50 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1">
              {es ? 'Motivo (opcional)' : 'Reason (optional)'}
            </label>
            <input
              type="text"
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
              placeholder={es ? 'Ej: lo pidió por teléfono' : 'e.g. requested by phone'}
              maxLength={500}
              className="w-full px-3 py-2 bg-slate-900/60 border border-slate-700/50 rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/40"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => { setShowAdd(false); setNewPhone(''); setNewReason(''); setPickedClientId(''); }}
              className="px-3 py-2 text-sm text-slate-300 hover:text-slate-100 transition"
            >
              {es ? 'Cancelar' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 text-slate-900 font-semibold rounded-lg text-sm transition"
            >
              {submitting ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Agregar' : 'Add')}
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="mt-4 p-3 bg-rose-500/10 border border-rose-500/30 text-rose-200 text-sm rounded-lg">
          {error}
        </div>
      )}

      <div className="mt-5 bg-slate-800/40 border border-slate-700/50 rounded-lg overflow-hidden">
        {loading && rows.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">
            {es ? 'Cargando…' : 'Loading…'}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <PhoneOff className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-400">
              {search
                ? (es ? 'Sin resultados.' : 'No matches.')
                : (es ? 'Ningún número en la lista todavía.' : 'No numbers on the list yet.')}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-900/40 text-slate-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">{es ? 'Número' : 'Number'}</th>
                <th className="text-left px-4 py-3 font-semibold">{es ? 'Origen' : 'Source'}</th>
                <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">{es ? 'Motivo' : 'Reason'}</th>
                <th className="text-left px-4 py-3 font-semibold whitespace-nowrap">{es ? 'Fecha' : 'Date'}</th>
                {canRemove && <th className="w-16"></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-700/40 hover:bg-slate-700/20">
                  <td className="px-4 py-3 text-slate-100 font-medium whitespace-nowrap">{fmtPhone(r.phone)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 text-xs rounded border ${SOURCE_BADGE[r.source] || SOURCE_BADGE.admin}`}>
                      {SOURCE_LABELS[r.source] || r.source}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-300 hidden md:table-cell max-w-xs truncate" title={r.reason || ''}>
                    {r.reason || <span className="text-slate-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmtDate(r.createdAt)}</td>
                  {canRemove && (
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleRemove(r)}
                        className="p-2 text-slate-500 hover:text-rose-400 transition"
                        title={es ? 'Volver a permitir SMS (audit-loggeado)' : 'Re-enable texting (audit-logged)'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > rows.length && (
        <div className="mt-3 text-xs text-slate-500 text-center">
          {es
            ? `Mostrando ${rows.length} de ${total}. Refina la búsqueda para ver más.`
            : `Showing ${rows.length} of ${total}. Refine search to see more.`}
        </div>
      )}
    </div>
  );
}
